<template>
  <div class="bg-gray-100">
    <div class="w-full lg:w-1/2">

      <div class="w-full max-w-xl mx-auto my-8">
        <BrokerpadLogo />

        <form v-on:submit.prevent class="bg-white my-6 rounded shadow-lg border" autocomplete="on">

          <div class="flex flex-wrap" style="border-radius: 8px 8px 0 0;">
            <div class="w-1/2">
              <a href="#"
                @click.prevent="registerAs = 'broker'"
                :class="{ 
                'block p-8 text-base text-gray-700 flex items-center' : true, 
                'bg-white' : registerAs == 'broker', 
                'bg-gray-200 border-b border-r border-gray-300' : registerAs != 'broker' 
              }">

                <img src="https://img.icons8.com/cotton/64/000000/business-group.png" class="inline w-8 mr-3" />
                Register as a <span class="font-semibold ml-2 underline">Broker</span>
              </a>
            </div>

            <div class="w-1/2">
              <a href="#"
                @click.prevent="registerAs = 'client'"
                :class="{ 
                'block p-8 text-base text-gray-700 flex items-center' : true, 
                'bg-white' : registerAs == 'client', 
                'bg-gray-200 border-b border-l border-gray-300' : registerAs != 'client' 
              }">

                <img src="https://img.icons8.com/cotton/64/000000/family.png" class="inline w-8 mr-3" />
                Register as a <span class="font-semibold ml-2 underline">Client</span>
              </a>
            </div>
          </div>

          <div class="p-8">
            <template v-if="registerByClient">
              <div class="text-xl uppercase font-semibold text-gray-700">
                Trying to access your application form?
              </div>

              <div class="mt-6 leading-normal text-base">
                If you're trying to access your application form which was emailed by your broker,
                please click on the button below and we can send you an email with a link to the
                application form again.
              </div>

              <div class="mt-4">
                <button
                  v-on:click="sendEmailLink"
                  class="text-white w-full py-4 text-base bg-blue-500 hover:bg-blue-600 font-semibold outline-none focus:outline-none">
                  Send me an email link
                </button>
              </div>

              <div class="mt-12 leading-normal text-base">
                If you really are a broker and would like to create an account for {{ getAppName }} please
                confirm by clicking on the button below.
              </div>

              <div class="mt-4">
                <button
                  v-on:click="register"
                  class="text-white w-full py-4 text-base bg-green-500 hover:bg-green-600 font-semibold outline-none focus:outline-none">
                  <template v-if="loading"><img src="/static/img/ajax-loader.gif" class="inline" /></template>
                  <template v-else>I am a broker. I'd like to sign up.</template>
                </button>
              </div>
            </template>

            <template v-else>
              <div class="text-2xl font-light text-gray-700">
                Create Your {{ registerAs == 'broker' ? 'Broker' : 'Client' }} Account
              </div>
              
              <div class="flex flex-wrap label-line">
                <div class="w-full lg:w-1/2 pr-0 lg:pr-1">
                  <div class="">
                    <label class="text-base">First name</label>
                  </div>

                  <div class="mt-2">
                    <input type="text"
                      v-model="firstName"
                      ref='firstName'
                      class="input-standard"
                      :disabled="loading" />

                    <div v-if="errors.firstName !== null"
                      class="text-red-500 mt-2 text-sm">{{ errors.firstName }}</div>
                  </div>
                </div>

                <div class="w-full lg:w-1/2 pl-0 lg:pl-1 mt-8 lg:mt-0">
                  <div class="">
                    <label class="text-base">Last name</label>
                  </div>

                  <div class="mt-2">
                    <input type="text"
                      v-model="lastName"
                      ref='lastName'
                      class="input-standard"
                      :disabled="loading" />

                    <div v-if="errors.lastName !== null"
                      class="text-red-500 mt-2 text-sm">{{ errors.lastName }}</div>
                  </div>
                </div>
              </div>

              <div class="mt-8">
                <label class="text-base">Email</label>
              </div>

              <div class="mt-2">
                <input type="text"
                  v-model="email"
                  ref='email'
                  class="input-standard"
                  :disabled="loading" />

                <div v-if="errors.email !== null"
                  class="text-red-500 mt-2 text-sm">{{ errors.email }}</div>
              </div>

              <div class="mt-8">
                <label class="text-base">Mobile phone</label>
              </div>

              <div class="mt-2">
                <input type="text"
                  ref='mobilePhone'
                  v-bind:value="mobilePhone"
                  class="input-standard"
                  v-on:keyup="format($event.target.value)"
                  :disabled="loading" />

                <div v-if="errors.mobilePhone !== null"
                  class="text-red-500 mt-2 text-sm">{{ errors.mobilePhone }}</div>
              </div>

              <div class="mt-8">
                <label class="text-base">Password</label>
              </div>

              <div class="mt-2">
                <InputBrokerpadPassword 
                  v-bind:value="password"
                  v-bind:updatePassword="updatePassword"></InputBrokerpadPassword>

                <div v-if="errors.password !== null"
                  class="text-red-500 mt-2 text-sm">{{ errors.password }}</div>
              </div>

              <div class="mt-8">
                <label class="text-base">Confirm password</label>
              </div>

              <div class="mt-2">
                <input type="password"
                  v-model="confirmPassword"
                  class="input-standard"
                  :disabled="loading" />

                <div v-if="errors.confirmPassword !== null"
                  class="text-red-500 mt-2 text-sm">{{ errors.confirmPassword }}</div>
              </div>

              <template v-if="registerAs == 'broker'">

                <div class="label-line">
                  <label class="text-base">Send me the {{ getAppName }} report</label>
                </div>

                <div class="form-line">
                  <select class="select-standard" v-model="reportFrequency">
                    <option :value="null">Please select from below</option>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                    <option value="never">No thanks</option>
                  </select>
                </div>

                <div class="mt-8">
                  <label class="text-base">Promo code</label>
                </div>

                <div class="mt-2">
                  <input type="text"
                    v-model="promoCode"
                    ref='promoCode'
                    class="input-standard"
                    :disabled="loading" />

                  <div v-if="errors.promoCode !== null"
                    class="text-red-500 mt-2 text-sm">{{ errors.promoCode }}</div>
                </div>

                <div class="label-line">
                  <label class="text-base">How did you hear about us?</label>
                </div>

                <div class="form-line">
                  <select class="select-standard" v-model="source">
                    <option :value="null">Please select from below</option>
                    <option value="referred-by-colleague">Referred by colleague</option>
                    <option value="facebook">Facebook Ad</option>
                    <option value="google">Google Ad</option>
                    <option value="linkedin">LinkedIn Ad</option>
                    <option value="youtube">YouTube Ad</option>
                    <option value="finance-and-coffee">Finance &amp; Coffee</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <template v-if="source == 'other'">
                  <div class="label-line">
                    <label class="text-base">Please specify</label>
                  </div>

                  <div class="mt-2" v-if="source == 'other'">
                    <input type="text"
                      v-model="sourceOther"
                      ref='sourceOther'
                      class="input-standard"
                      :disabled="loading" />
                  </div>
                </template>
              </template>

              <div class="mt-12 text-base">
                <div class="flex items-center">
                  <input type="checkbox" class="mr-2" id="input-terms" v-model="termsAgreed" />
                  <label for="input-terms" class="text-gray-800">
                    By registering, I agree to the {{ getAppName }}'s
                    <a href="https://www.brokerpad.com.au/terms-of-service" target="_blank" class="text-blue-600 underline">Terms of Service</a>.
                  </label>
                </div>

                <div v-if="errors.termsAgreed !== null" class="text-red-500 mt-2 text-sm">
                  {{ errors.termsAgreed }}
                </div>
              </div>

              <div class="mt-2 text-base">
                <div class="flex items-center">
                  <input type="checkbox" class="mr-2" id="input-privacy" v-model="privacyAgreed" />
                  <label for="input-privacy" class="text-gray-800">
                    By registering, I agree to the {{ getAppName }}'s
                    <a href="https://www.brokerpad.com.au/privacy-policy" target="_blank" class="text-blue-600 underline">Privacy Policy</a>.
                  </label>
                </div>

                <div v-if="errors.privacyAgreed !== null" class="text-red-500 mt-2 text-sm">
                  {{ errors.privacyAgreed }}
                </div>
              </div>

              <div class="button-line">
                <button
                  v-on:click="submit"
                  class="text-white w-full py-4 text-base bg-blue-500 hover:bg-blue-600 font-semibold">
                  <template v-if="loading"><img src="/static/img/ajax-loader.gif" class="inline" /></template>
                  <template v-else>Get Started!</template>
                </button>
              </div>
            </template>
          </div>
        </form>

        <div class="pl-8 mt-6">
          <PublicFooter />
        </div>
      </div>
    </div>

    <div class="hidden lg:block w-1/2 bg-gray-200"
      style="position:fixed;top:0;right:0;height:100%;">

      <div class="flex justify-center items-center h-full w-2/3 mx-auto">
        <carousel :perPage="1" :paginationEnabled="true" :paginationColor="'#007BD0'" :autoplay="true" :autoplayTimeout="4000">
          <slide>
            <div class="">
              <img src="/static/img/Confirmation.png" />
            </div>

            <div class="text-center text-xl text-gray-800" style="font-family: Martel;">
              Free Lead Capture Form
            </div>

            <div class="mt-2 text-base text-gray-700 text-center">
              Embed our lead capture form on your own website and get new clients.
            </div>
          </slide>

          <slide>
            <div class="">
              <img src="/static/img/Workflow.png" />
            </div>

            <div class="text-center text-xl text-gray-800" style="font-family: Martel;">
              Fact Finding at the Speed of Light
            </div>

            <div class="mt-2 text-base text-gray-700 text-center">
              Save up to 5 hours per client by streamlining your fact finding process.
            </div>
          </slide>

          <slide>
            <div class="">
              <img src="/static/img/Templates.png" />
            </div>

            <div class="text-center text-xl text-gray-800" style="font-family: Martel;">
              Use Templates for Super Power
            </div>

            <div class="mt-2 text-base text-gray-700 text-center">
              Remove repetitive tasks by using our templates for faster data collection.
            </div>
          </slide>

          <slide>
            <div class="">
              <img src="/static/img/Documents.png" />
            </div>

            <div class="text-center text-xl text-gray-800" style="font-family: Martel;">
              Collect the Documents You Need
            </div>

            <div class="mt-2 text-base text-gray-700 text-center">
              Get all the right documents you need quickly with our powerful file collector.
            </div>
          </slide>

          <slide>
            <div class="">
              <img src="/static/img/Integration.png" />
            </div>

            <div class="text-center text-xl text-gray-800" style="font-family: Martel;">
              Integrate with Your Existing Tools
            </div>

            <div class="mt-2 text-base text-gray-700 text-center">
              {{ getAppName }} plays well with Mercury, BankStatements and more.
            </div>
          </slide>
        </carousel>
      </div>
    </div>

    <br/><br/>
  </div>
</template>

<script>
  import validator                from 'validator'
  import { EventBus }             from '@/shared/event-bus'
  import InputBrokerpadPassword   from '@/shared/components/form/InputBrokerpadPassword'
  import PublicFooter             from '@/shared/components/public/Footer'
  import BrokerpadLogo            from '@/shared/components/BrokerpadLogo'
  import { Carousel, Slide }      from 'vue-carousel'

  // function to reset errors
  function resetErrors() {
    return {
      firstName      : null,
      lastName       : null,
      email          : null,
      mobilePhone    : null,
      password       : null,
      confirmPassword: null,
      promoCode      : null,
      termsAgreed    : null,
      privacyAgreed  : null,
    }
  }

	export default {

    components : {
      InputBrokerpadPassword,
      PublicFooter,
      BrokerpadLogo,
      Carousel,
      Slide
    },

		data () {
      return {
        registerAs      : 'broker',
        loading         : false,
        firstName       : null,//process.env.NODE_ENV == 'development' ? 'a' : null,
        lastName        : null,//process.env.NODE_ENV == 'development' ? 'a' : null,
        email           : null,//process.env.NODE_ENV == 'development' ? 'haylee51@yahoo.com' : null,
        mobilePhone     : null,//process.env.NODE_ENV == 'development' ? 111 : null,
        password        : null,//process.env.NODE_ENV == 'development' ? 'Hello123!' : null,
        confirmPassword : null,//process.env.NODE_ENV == 'development' ? 'Hello123!' : null,
        reportFrequency : 'weekly',
        promoCode       : this.$route.params.referralCode != null ? this.$route.params.referralCode : null,
        source          : null,
        sourceOther     : null,
        submitting      : false,
        termsAgreed     : null,//process.env.NODE_ENV == 'development' ? true : null,
        privacyAgreed   : null,//process.env.NODE_ENV == 'development' ? true : null,
        registerByClient: false,
        registeredClient: null,
        errors          : resetErrors(),
      }
    },

    created() {
      if(this.$route.query.email != null) {
        this.email = this.$route.query.email
      }
    },

    mounted() {
      this.$refs.firstName.focus()
      document.body.className = 'bg-gray-100'
    },

		methods: {

      updatePassword(params) {
        if(params != null && params.value != null) {
          this.password = params.value
        }
      },

      format(value) {
        let formatted = this.formatPhoneNumber(value)
        this.mobilePhone = formatted
      },

	    submit() {
        // remove register errors
        let valid    = true;
        this.errors  = resetErrors()

        if(this.firstName == null || this.firstName.length == 0) {
          this.errors.firstName = "Please type a valid first name."
          valid = false
        }

        if(this.lastName == null || this.lastName.length == 0) {
          this.errors.lastName = "Please type a valid first name."
          valid = false
        }

        if(this.email == null || !validator.isEmail(this.email)) {
          this.errors.email = "Please type a valid email."
          valid = false
        }

        // if(this.mobilePhone == null || this.mobilePhone.length == 0 || !validator.isNumeric(this.mobilePhone.replace(" ", ""))) {
        if(this.mobilePhone == null || this.mobilePhone.length == 0) {
          this.errors.mobilePhone = "Please type a valid mobile phone number."
          valid = false
        }

        if(this.password == null || this.password.length == 0) {
          this.errors.password = "Please type a valid password."
          valid = false
        }

        if(this.confirmPassword == null || this.confirmPassword.length == 0) {
          this.errors.confirmPassword = "Please type a valid confirm password."
          valid = false
        }

        if(this.password != this.confirmPassword) {
          this.errors.confirmPassword = "Password do not match."
          valid = false
        }

        if(!this.termsAgreed) {
          this.errors.termsAgreed = "Please agree to our Terms of Service before registering"
          valid = false
        }

        if(!this.privacyAgreed) {
          this.errors.privacyAgreed = "Please read and agree to our Privacy Policy before registering"
          valid = false
        }

        if(valid) {
          this.loading = true

          this.apiPost({
            email : this.email.toLowerCase().trim(),
          }, 'auth/register-by-client', (data) => {

            if(data.error != null) {
              this.loading = false
              this.errors.email = data.error
            }
            
            else if(data.client != null) {
              this.loading          = false
              this.registerByClient = true
              this.registeredClient = data.client
            }

            else {
              this.register()
            }
          })
        }
	    },

      register() {
        this.loading = true
        this.apiPost({
          registerAs     : this.registerAs,
          firstName      : this.firstName,
          lastName       : this.lastName,
          email          : this.email.toLowerCase().trim(),
          mobilePhone    : this.mobilePhone,
          password       : this.password,
          reportFrequency: this.reportFrequency,
          promoCode      : this.promoCode,
          source         : this.source,
          sourceOther    : this.sourceOther
        }, 'auth/register', (data) => {
          this.loading = false

          this.$router.push({
            name : 'auth-confirm-registration'
          })
          
          // localStorage.setItem("bp-broker-jwt", data.token)
          // this.$store.commit('setIsLoggedIn', true)

          // this.getUser({
          //   force : true
          // }, (user) => {
          //   if(user != null) {

          //     if(this.registerAs == 'broker') {
          //       this.$router.push({
          //         name  : 'broker-home', 
          //         params: {
          //           showConfirmRegistration : 1
          //         }
          //       })
          //     }

          //     else {
          //       this.$router.push({
          //         name: 'client-overview',
          //         params : {
          //           clientId : data.client.id
          //         }
          //       }) 
          //     }
          //   }
          // })
        })
      },

      sendEmailLink() {
        this.apiPost({
          clientId: this.registeredClient.id,
          email   : this.email.toLowerCase().trim(),
        }, 'auth/send-link-to-client', (data) => {
          this.$router.push({
            name : 'auth-link-sent-to-client'
          })
        })
      }
		}
	}
</script>